// Route pattern matching. Dependency-free and DOM-free so it can be unit-tested
// under bun — router.js instantiates a Router at module scope, which touches
// window/document and therefore can't be imported into the test runtime at all.
// Same reasoning as api-response.js.

/**
 * Compiles a route pattern into a matcher. `:param` segments become capture
 * groups.
 * @param {string} pattern
 * @param {string} name
 * @param {object} meta
 */
export function compileRoute(pattern, name, meta = {}) {
    const paramNames = [];
    const regexPattern = pattern.replace(/:(\w+)/g, (_, paramName) => {
        paramNames.push(paramName);
        return '([^/]+)';
    });
    return { pattern, regex: new RegExp(`^${regexPattern}$`), paramNames, name, meta };
}

/**
 * Finds the first route matching a path and extracts its params.
 * @param {Array<ReturnType<typeof compileRoute>>} routes
 * @param {string} path
 * @returns {{name: string, params: {[key: string]: string}, meta: object}|null}
 */
export function matchRoute(routes, path) {
    for (const route of routes) {
        const match = path.match(route.regex);
        if (!match) continue;

        const params = {};
        route.paramNames.forEach((name, index) => {
            const value = match[index + 1];
            if (value) params[name] = decodeURIComponent(value);
        });

        return { name: route.name, params, meta: route.meta };
    }

    return null;
}

// The route table lives here, not in router.js, so tests assert the real thing
// rather than a copy that can silently drift from it. This console is entirely
// staff-only: the invitee accept flow and the legal documents it once served now
// live in d3-auth, so there is no public surface here anymore.
export const ROUTES = [
    compileRoute('/', 'invitations', { requiresAuth: true }),
    compileRoute('/changelog', 'changelog', { requiresAuth: true }),
    // Before /changelog/:id, or "new" would match as a post id.
    compileRoute('/changelog/new', 'changelog-new', { requiresAuth: true }),
    compileRoute('/changelog/:id', 'changelog-edit', { requiresAuth: true }),
    compileRoute('/polls', 'polls', { requiresAuth: true }),
    // Before /polls/:id, or "new" would match as a poll id.
    compileRoute('/polls/new', 'poll-new', { requiresAuth: true }),
    compileRoute('/polls/:id', 'poll-edit', { requiresAuth: true }),
    compileRoute('/polls/:id/results', 'poll-results', { requiresAuth: true }),
    compileRoute('/lists', 'lists', { requiresAuth: true }),
    compileRoute('/lists/:id', 'list-contact', { requiresAuth: true }),
    // A stable link: d3-community's staff Discord announcements point here with
    // an early-access entry id. It redirects to that person's page.
    compileRoute('/beta-queue/:id', 'beta-queue-entry', { requiresAuth: true }),
    compileRoute('/analytics', 'analytics', { requiresAuth: true }),
    compileRoute('/testing', 'et-plans', { requiresAuth: true }),
    compileRoute('/testing/plans/:planId', 'et-plan', { requiresAuth: true }),
    compileRoute('/testing/items/:itemId', 'et-item', { requiresAuth: true }),
    compileRoute('/auth/callback', 'auth-callback'),
];
